import { ARCHIVE_FILTER_TO_URL_PARAM, serviceToClientPlan } from "../../models/plans/index";
import { convertTimeToUnixTimestamp } from "../../utils/index";
import axios from "axios";
import { Poller } from "../../handlers/poller";
import { NoAuthorizedUser } from "../../errors";
export const POLL_INTERVAL = 7000;
const getMostRecentPlanUpdate = (pollResult) => {
    return pollResult.reduce((newest, plan) => {
        const planLastUpdated = plan.lastUpdated.getTime();
        return planLastUpdated > (newest || 0) ? planLastUpdated : newest;
    }, 0);
};
class PlanPoller extends Poller {
    constructor(sdk, appliedFilters, config) {
        super(sdk, {
            data: config?.data,
            interval: config?.interval || POLL_INTERVAL
        });
        /**
         * Guard flag to prevent concurrent API requests.
         * When a poll is triggered while a request is already in flight (e.g., on slow 3G connections),
         * the new poll will be skipped to avoid duplicate requests to the server.
         */
        this.isFetching = false;
        /**
         * Exponential backoff support.
         * Tracks the number of polls completed to calculate the next interval: 7s, 14s, 28s, 56s...
         */
        this.pollCount = 0;
        /**
         * Timer ID for the currently scheduled poll.
         * Stored so we can cancel it when stop() or restartPoll() is called.
         */
        this.pollIntervalId = null;
        this.appliedFilters = appliedFilters;
        /**
         * starting with a null mostRecentUpdate timestamp ensures that when
         * polling plans begins, the first api call will request all plans, not
         * just plans after a given time.
         */
        this.mostRecentPlanUpdate = null;
    }
    /**
     * Fetches plans from the API based on the mostRecentPlanUpdate timestamp.
     *
     * FIRST POLL (mostRecentPlanUpdate is null):
     * - Fetches ALL plans with min_last_updated_at=0 (or sinceDate if provided)
     * - This grabs the complete set of plans to initialize the poller state
     *
     * SUBSEQUENT POLLS (mostRecentPlanUpdate is set):
     * - Fetches ONLY UPDATED plans with min_last_updated_at=<timestamp+1>
     * - This checks for plans that have been modified since the last poll
     * - Reduces unnecessary data transfer by only fetching changes
     */
    async fetchData() {
        const { archiveStatus, allUsers, sinceDate } = this.appliedFilters;
        // If sinceDate is not provided, get all plans since unix OS inception.
        const getPlansSince = sinceDate ? sinceDate : 0;
        const plansUpdatedSinceLastPoll = await axios.get(`${this.sdk.urls.basePlannerServiceUrl}/plans?min_last_updated_at=${this.mostRecentPlanUpdate
            ? convertTimeToUnixTimestamp(this.mostRecentPlanUpdate) + 1
            : getPlansSince}${archiveStatus ? `&statuses=${ARCHIVE_FILTER_TO_URL_PARAM[archiveStatus]}` : ""}${allUsers ? `&all_users=${allUsers}` : ""}`);
        if (plansUpdatedSinceLastPoll.data.data === null) {
            return [];
        }
        return plansUpdatedSinceLastPoll.data.data.map(serviceToClientPlan);
    }
    async requestPlan(planId) {
        if (!planId)
            throw new Error("Plan does not exist");
        return axios
            .get(`${this.sdk.urls.basePlannerServiceUrl}/plan/${planId}`)
            .then((res) => res.data);
    }
    shouldEmitPlanUpdate(update) {
        if (!this.data?.size)
            return true;
        if (!update.length)
            return false;
        if (this.appliedFilters.currentPlanId) {
            const currentPlanInState = this.data.get(this.appliedFilters.currentPlanId);
            if (!currentPlanInState)
                return true;
            return currentPlanInState.lastUpdated < update[0].lastUpdated;
        }
        return true;
    }
    /**
     * Overrides the base poll method to implement exponential backoff.
     * Formula: POLL_INTERVAL * 2^pollCount (7s, 14s, 28s, 56s, 112s...)
     */
    poll() {
        // Local function needed for recursive scheduling of subsequent poll cycles.
        // Each poll schedules the next one via setTimeout(() => executePollCycle(), ...).
        // We can't call poll() recursively because that would create a new Subject each time.
        const executePollCycle = () => {
            this.handlePolling()
                .catch((error) => {
                if (error.response?.status === 403 || error.response?.status === 401) {
                    this.subject.error(new NoAuthorizedUser());
                    this.stop();
                    return;
                }
                if (axios.isAxiosError(error)) {
                    this.subject.error(error);
                }
                else {
                    throw error;
                }
            })
                .finally(() => {
                // Schedule next poll with exponential backoff to check for updated plans
                // Calculate interval BEFORE incrementing pollCount to get correct progression:
                // 1st poll done (pollCount=0) → next at 7s * 2^0 = 7s
                // 2nd poll done (pollCount=1) → next at 7s * 2^1 = 14s
                // 3rd poll done (pollCount=2) → next at 7s * 2^2 = 28s
                const nextInterval = POLL_INTERVAL * Math.pow(2, this.pollCount);
                this.pollCount++;
                this.pollIntervalId = setTimeout(executePollCycle, nextInterval);
            });
        };
        // Execute first poll immediately to get all plans, then schedule subsequent polls for updated plans.
        executePollCycle();
        return this.subject;
    }
    async handlePolling() {
        // Skip this poll if a fetch is already in progress
        if (this.isFetching) {
            return;
        }
        this.isFetching = true;
        try {
            if (this.appliedFilters.currentPlanId) {
                const currentPlan = await this.requestPlan(this.appliedFilters.currentPlanId).then((res) => serviceToClientPlan(res));
                const plans = [currentPlan];
                if (this.shouldEmitPlanUpdate(plans)) {
                    this.updateSubscribers(plans);
                    this.subject.next(plans);
                }
            }
            else {
                const mostRecentPlans = await this.fetchData();
                const newestTimestamp = getMostRecentPlanUpdate(mostRecentPlans);
                if (newestTimestamp) {
                    // Update the timestamp so the next poll will only fetch plans updated after this time.
                    // This transitions from "fetch all plans" (first poll) to "fetch updated plans only" (subsequent polls).
                    this.mostRecentPlanUpdate = newestTimestamp;
                }
                if (this.shouldEmitPlanUpdate(mostRecentPlans)) {
                    this.updateSubscribers(mostRecentPlans);
                }
            }
        }
        finally {
            this.isFetching = false;
        }
    }
    /**
     * Forces the poll to revert to its original state without stopping. It
     * will continue to update its subscribers. Filters are not reset.
     */
    restartPoll() {
        // Clear exponential backoff timer
        if (this.pollIntervalId) {
            clearTimeout(this.pollIntervalId);
            this.pollIntervalId = null;
        }
        this.pollCount = 0;
        this.reset();
        this.mostRecentPlanUpdate = null;
        this.isFetching = false;
        return this.poll();
    }
    /**
     * Stops the poller and clears exponential backoff timer.
     */
    stop() {
        if (this.pollIntervalId) {
            clearTimeout(this.pollIntervalId);
            this.pollIntervalId = null;
        }
        this.pollCount = 0;
        super.stop();
    }
}
export default PlanPoller;
//# sourceMappingURL=poller.js.map